import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  ChevronLeft,
  ChevronDown,
  Search,
  Eye,
  Users,
  X,
  CheckCircle2,
  Clock,
  FileQuestion,
  Download,
  FileSpreadsheet,
  TrendingUp,
  ClipboardList,
  ChevronRight,
} from "lucide-react";
import { reportData } from "../../../../Redux/Teacher_Panel/report/report.action";
import "./reportDetailedView.scss";
import { analyticsReport } from "../../../../Redux/analytics/analytics.action";
import { formatDateTime } from "../../../../utils/utils";
import {
  pal,
  hex2rgba,
  gradeBadge,
} from "../../../../Reusable_Components/Report";
import { profileDetails } from "../../../../Redux/profile/profile.action";

/* ── generic helpers ── */
function isAttempted(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") return v.trim().toLowerCase() === "attempted";
  return false;
}

function getClassName(a) {
  return a.assigned_class || a.class_name || a.class || a.standard || "—";
}
function getDivision(a) {
  return a.assigned_division || a.division || a.section || "—";
}

/* ── Excel export helpers ── */
function safeSheetName(name, maxLen = 31) {
  // Excel sheet names: max 31 chars, no : \ / ? * [ ]
  return (
    String(name)
      .replace(/[:\\/?*[\]]/g, "")
      .slice(0, maxLen) || "Sheet"
  );
}

function uniqueSheetName(base, usedNames) {
  let name = safeSheetName(base);
  let n = 1;
  while (usedNames.has(name)) {
    name = safeSheetName(`${base}_${n++}`);
  }
  usedNames.add(name);
  return name;
}

// Auto-size columns so the exported sheet looks intentional, not raw-dumped
function autosizeColumns(ws, rows, minWidth = 8, maxWidth = 48) {
  if (!rows || !rows.length) return;
  const keys = Object.keys(rows[0]);
  ws["!cols"] = keys.map((k) => {
    let max = String(k).length;
    for (const r of rows) {
      const v = r[k];
      const len = v == null ? 0 : String(v).length;
      if (len > max) max = len;
    }
    return { wch: Math.min(Math.max(max + 2, minWidth), maxWidth) };
  });
}

function addSheet(wb, rows, sheetName, usedNames) {
  const ws = XLSX.utils.json_to_sheet(rows && rows.length ? rows : [{}]);
  autosizeColumns(ws, rows && rows.length ? rows : [{}]);
  const name = usedNames
    ? uniqueSheetName(sheetName, usedNames)
    : safeSheetName(sheetName);
  XLSX.utils.book_append_sheet(wb, ws, name);
  return name;
}

function timestamp() {
  return new Date().toISOString().slice(0, 10);
}

function downloadWorkbook(wb, fileNamePrefix) {
  // Manual Blob download instead of XLSX.writeFile — more reliable across
  // bundlers / CSPs / sandboxed iframes than the library's internal
  // "create+click a hidden link" helper.
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileNamePrefix}_${timestamp()}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Every exported row gets these two columns first, so whoever opens the
// sheet later (admin, parent, audit) can tell which teacher it came from
// without needing the filename or a cover sheet.
function withTeacherCols(row, teacherInfo) {
  return {
    "Teacher ID": teacherInfo?.id ?? "—",
    "Teacher Name": teacherInfo?.name ?? "—",
    ...row,
  };
}

/* ---------- Row builders ---------- */

function buildAssignmentSummaryRows(tests, teacherInfo) {
  return tests.map((t) =>
    withTeacherCols(
      {
        Subject: t.subject,
        "Assignment ID": t.assignment_id,
        Chapter: t.chapter,
        Class: t.className,
        Division: t.division,
        Date: t.date,
        Time: t.time,
        "Total Questions": t.totalNoOfQuestions,
        "Assigned to Students": t.assigned,
        "Attempted by Students": t.attempted,
        "Attendance %": t.attendancePct,
        // "Marks Secured": t.totalMarksSecured,
        "Total Max Marks": t?.totalNoOfQuestions,
        "Avg Score %": t.avgScore,
      },
      teacherInfo,
    ),
  );
}

function buildStudentRows(test, teacherInfo) {
  return test.students.map((s, i) =>
    withTeacherCols(
      {
        "Sr No": i + 1,
        "Student ID": s.studentID,
        "Student Name": s.name,
        Status: s.status === true ? "Attempted" : "Not Attempted",
        "Marks Secured": s.status === true ? (s.marks ?? "—") : "—",
        "Max Marks": s.status === true ? (s.maxMarks ?? "—") : "—",
        "Percentage %": s.status === true ? (s.percentage ?? 0) : "—",
      },
      teacherInfo,
    ),
  );
}

function buildQuestionRows(test, teacherInfo) {
  return (test.questions || []).map((q, i) =>
    withTeacherCols(
      {
        "Q No": i + 1,
        Type: q.type,
        Question: q.text,
        Options: q.options && q.options.length ? q.options.join(" | ") : "",
        "Correct Answer": q.answer,
        Marks: q.marks,
        Topic: q.topic || "",
      },
      teacherInfo,
    ),
  );
}

// Flat "one row per student per assignment" — the backbone of the Student Report
function buildStudentDetailRows(tests, teacherInfo) {
  const rows = [];
  tests.forEach((t) => {
    t.students.forEach((s) => {
      rows.push(
        withTeacherCols(
          {
            "Student ID": s.studentID,
            "Student Name": s.name,
            Class: t.className,
            Division: t.division,
            Subject: t.subject,
            "Assignment ID": t.assignment_id,
            Chapter: t.chapter,
            Date: t.date,
            Status: s.status === true ? "Attempted" : "Not Attempted",
            "Marks Secured": s.status === true ? (s.marks ?? "—") : "—",
            "Max Marks": s.status === true ? (s.maxMarks ?? "—") : "—",
            "Percentage %": s.status === true ? (s.percentage ?? 0) : "—",
          },
          teacherInfo,
        ),
      );
    });
  });
  return rows;
}

// One row per unique student — rolled up across every assignment passed in
function buildStudentSummaryRows(tests, teacherInfo) {
  const byStudent = new Map();

  tests.forEach((t) => {
    t.students.forEach((s) => {
      const key = `${s.studentID}__${s.name}`;
      if (!byStudent.has(key)) {
        byStudent.set(key, {
          studentID: s.studentID,
          name: s.name,
          classes: new Set(),
          divisions: new Set(),
          assigned: 0,
          attempted: 0,
          status: s.status,
          percentSum: 0,
          percentCount: 0,
          marksSum: 0,
          maxMarksSum: 0,
        });
      }
      const rec = byStudent.get(key);
      rec.classes.add(t.className);
      rec.divisions.add(t.division);
      rec.assigned += 1;
      if (s.status === true) {
        rec.attempted += 1;
        rec.percentSum += s.percentage || 0;
        rec.percentCount += 1;
        rec.marksSum += typeof s.marks === "number" ? s.marks : 0;
        rec.maxMarksSum += typeof s.maxMarks === "number" ? s.maxMarks : 0;
      }
    });
  });

  return Array.from(byStudent.values())
    .map((rec) =>
      withTeacherCols(
        {
          "Student ID": rec.studentID,
          "Student Name": rec.name,
          "Class(es)": Array.from(rec.classes).join(", "),
          "Division(s)": Array.from(rec.divisions).join(", "),
          "Tests Assigned": rec.assigned,
          "Tests Attempted": rec.attempted,
          "Attendance %": rec.assigned
            ? Math.round((rec.attempted / rec.assigned) * 100)
            : 0,
          "Total Marks Secured": rec.marksSum,
          // "Total Max Marks": rec.totalMaxMarks,
          //  "Total Max Marks": rec.totalNoOfQuestions,
          Status: rec.status === true ? "Attempted" : "Not Attempted",
          "Avg Score % (attempted only)": rec.percentCount
            ? Math.round(rec.percentSum / rec.percentCount)
            : "—",
        },
        teacherInfo,
      ),
    )
    .sort((a, b) => a["Student Name"].localeCompare(b["Student Name"]));
}

// One row per Class + Division + Subject — the roll-up teachers/admins scan first
function buildClassSummaryRows(tests, teacherInfo) {
  const byGroup = new Map();

  tests.forEach((t) => {
    const key = `${t.className}__${t.division}__${t.subject}`;
    if (!byGroup.has(key)) {
      byGroup.set(key, {
        className: t.className,
        division: t.division,
        subject: t.subject,
        testsCount: 0,
        assigned: 0,
        attempted: 0,
        scoreSum: 0,
        marksSum: 0,
        maxMarksSum: 0,
      });
    }
    const rec = byGroup.get(key);
    rec.testsCount += 1;
    rec.assigned += t.assigned;
    rec.attempted += t.attempted;
    rec.scoreSum += t.avgScore;
    rec.marksSum += t.totalMarksSecured || 0;
    // rec.maxMarksSum += t.totalMaxMarks || 0;
     rec.maxMarksSum += t?.totalNoOfQuestions || 0;
  });

  return Array.from(byGroup.values())
    .map((rec) =>
      withTeacherCols(
        {
          Class: rec.className,
          Division: rec.division,
          Subject: rec.subject,
          "Tests Conducted": rec.testsCount,
          "Assigned to Students": rec.assigned,
          "Attempted by Students": rec.attempted,
          "Attendance %": rec.assigned
            ? Math.round((rec.attempted / rec.assigned) * 100)
            : 0,
          // "Total Marks Secured": rec.marksSum,
          // "Total Max Marks": rec.maxMarksSum,
          "Avg Score %": rec.testsCount
            ? Math.round(rec.scoreSum / rec.testsCount)
            : 0,
        },
        teacherInfo,
      ),
    )
    .sort(
      (a, b) =>
        a.Class.localeCompare(b.Class) ||
        a.Division.localeCompare(b.Division) ||
        a.Subject.localeCompare(b.Subject),
    );
}

/* ---------- Report generators ---------- */

// 1) Assignment Report — one sheet per assignment (roster + question paper), plus a summary sheet
function exportAssignmentReport(
  tests,
  teacherInfo,
  fileNamePrefix = "Assignment_Report",
) {
  if (!tests?.length) return;
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();

  addSheet(
    wb,
    buildAssignmentSummaryRows(tests, teacherInfo),
    "Summary",
    usedNames,
  );

  tests.forEach((t) => {
    const base = `${t.assignment_id}_${t.className}${t.division}`.slice(0, 20);
    addSheet(
      wb,
      buildStudentRows(t, teacherInfo),
      `${base}_Students`,
      usedNames,
    );
    addSheet(
      wb,
      buildQuestionRows(t, teacherInfo),
      `${base}_QPaper`,
      usedNames,
    );
  });

  downloadWorkbook(wb, fileNamePrefix);
}

// 2) Student Report — rollup per student + a full flat detail log
function exportStudentReport(
  tests,
  teacherInfo,
  fileNamePrefix = "Student_Report",
) {
  if (!tests?.length) return;
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();

  addSheet(
    wb,
    buildStudentSummaryRows(tests, teacherInfo),
    "Student Summary",
    usedNames,
  );
  addSheet(
    wb,
    buildStudentDetailRows(tests, teacherInfo),
    "Student Detail Log",
    usedNames,
  );

  downloadWorkbook(wb, fileNamePrefix);
}

// 3) Class / Subject Summary Report — aggregated view + underlying assignment list
function exportClassSummaryReport(
  tests,
  teacherInfo,
  fileNamePrefix = "Class_Summary_Report",
) {
  if (!tests?.length) return;
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();

  addSheet(
    wb,
    buildClassSummaryRows(tests, teacherInfo),
    "Class Summary",
    usedNames,
  );
  addSheet(
    wb,
    buildAssignmentSummaryRows(tests, teacherInfo),
    "Assignment Breakdown",
    usedNames,
  );

  downloadWorkbook(wb, fileNamePrefix);
}

/* ═══════════════════════════ Component ═══════════════════════════ */

export default function ReportDetailedView() {
  const dispatch = useDispatch();
  const nav = useNavigate();

  const reportDataStore = useSelector(
    (s) => s?.reportManager?.reportDataArray?.assignments,
  );

  const teacherInfo = useSelector((s) => {
    const teacherName =
      `${s?.ProfileManager?.user_details?.first_name || ""} ${s?.ProfileManager?.user_details?.last_name || ""}`.trim();

    return {
      name: teacherName,
      id: s?.ProfileManager?.user_details?.id,
    };
  });

  useEffect(() => {
    dispatch(reportData());
    dispatch(profileDetails());
  }, [dispatch]);

  // filters
  const [classFilter, setClassFilter] = useState("All");
  const [divisionFilter, setDivisionFilter] = useState("All");
  const [subjectFilter, setSubjectFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All"); // All | Attempted | Pending
  const [search, setSearch] = useState("");

  const [dateFrom, setDateFrom] = useState(""); // "YYYY-MM-DD"
  const [dateTo, setDateTo] = useState(""); // "YYYY-MM-DD"

  const [expandedId, setExpandedId] = useState(null);

  const [selectedTest, setSelectedTest] = useState(null);
  const [detailQuestions, setDetailQuestions] = useState([]);

  // row selection for Excel export
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Report Builder modal
  const [builderOpen, setBuilderOpen] = useState(false);
  const [reportType, setReportType] = useState("assignment"); // assignment | student | class
  const [reportScope, setReportScope] = useState("filtered"); // all | filtered | selected
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (builderOpen) {
      setReportScope(selectedIds.size > 0 ? "selected" : "filtered");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderOpen]);

  function normalizeQuestions(a) {
    const ad = a.assignment_data || {};

    const mcq = (ad.mcq || []).map((q) => ({
      id: q.question_id,
      type: "MCQ",
      text: q.question,
      options: q.options || [],
      correct: q.correct_answer,
      answer: q.correct_answer,
      marks: q.marks ?? 1,
      topic: q.topic,
      explanation: q.explanation,
    }));

    const short = (ad.short_answer || []).map((q) => ({
      id: q.question_id,
      type: "SHORT",
      text: q.question,
      answer: q.correct_answer,
      marks: q.marks ?? 1,
      topic: q.topic,
      explanation: q.explanation,
    }));

    const fib = (ad.fill_in_the_blank || []).map((q) => ({
      id: q.question_id,
      type: "FIB",
      text: q.question,
      answer: q.correct_answer,
      marks: q.marks ?? 1,
      topic: q.topic,
      explanation: q.explanation,
    }));

    return [...mcq, ...short, ...fib];
  }

  function normalizeStudentAnswers(s) {
    const raw = s.student_assignment_data;
    if (!raw || !raw.length) return null;

    const typeMap = {
      mcq: "MCQ",
      short_answer: "SHORT",
      fill_in_the_blank: "FIB",
    };

    return raw.map((q, i) => ({
      id: q.question_id ?? i,
      type:
        typeMap[q.question_type] || q.question_type?.toUpperCase() || "SHORT",
      text: q.question,
      options: q.options || [],
      correct: q.correct_answer,
      answer: q.correct_answer,
      userAnswer: q.user_answer,
      evaluation: q.evaluation, // "correct" | "incorrect"
      marksAwarded: q.marks_awarded,
      marks: q.max_marks ?? 1,
      topic: q.topic,
      explanation: q.explanation,
    }));
  }

  /* ── Build one row per test/assignment ── */
  const testList = useMemo(() => {
    if (!reportDataStore?.length) return [];
    return reportDataStore.map((a, idx) => {
      const students = (a.student_details || []).map((s, i) => ({
        name: s.student_name || s.name || `Student ${i + 1}`,
        studentID: s.student_id || "—",
        marks: s.marks_obtained ?? null,
        // maxMarks: s.max_marks ?? a.max_marks ?? null,
        maxMarks: a?.total_questions ?? null,
        percentage:
          s.percentage ??
          (s.max_marks
            ? Math.round((s.marks_obtained / s.max_marks) * 100)
            : 0),
        status: isAttempted(s.attempted),
        answers: normalizeStudentAnswers(s),
      }));
      const attemptedStudents = students.filter((s) => s.status === true);
      const avgScore = attemptedStudents.length
        ? Math.round(
            attemptedStudents.reduce((sum, s) => sum + (s.percentage || 0), 0) /
              attemptedStudents.length,
          )
        : 0;
      // Marks secured — summed only across students who actually attempted,
      // so a class full of "Not Attempted" rows doesn't drag this to 0/0.
      const totalMarksSecured = attemptedStudents.reduce(
        (sum, s) => sum + (typeof s.marks === "number" ? s.marks : 0),
        0,
      );
      const totalMaxMarks = attemptedStudents.reduce(
        (sum, s) => sum + (typeof s.maxMarks === "number" ? s.maxMarks : 0),
        0,
      );
   
      const assigned = a.no_of_students_assigned ?? students.length;
      const attempted = a.no_of_students_attempted ?? attemptedStudents.length;
      const {
        date,
        time,
        raw: assignedDateObj,
      } = formatDateTime(a.assigned_date || a.created_date);
      return {
        id: a.id ?? a._id ?? idx,
        assignment_id: a.assignment_id || "Unknown",
        subject: a.subject || "Unknown",
        chapter: a.chapter || a.topic || "Unknown",
        topic: a.topic || a.chapter || "Unknown",
        className: getClassName(a),
        division: getDivision(a),
        date, // formatted display date
        time, // formatted display time
        assignedDateObj, // real Date for filtering/sorting
        assigned,
        attempted,
        attendancePct: assigned ? Math.round((attempted / assigned) * 100) : 0,
        avgScore,
        totalMarksSecured,
        totalMaxMarks,
        students,
        questions: normalizeQuestions(a),
        totalNoOfQuestions: a.total_questions ?? normalizeQuestions(a).length,
      };
    });
  }, [reportDataStore]);

  const classOptions = useMemo(
    () => [
      "All",
      ...Array.from(new Set(testList.map((t) => t.className))).sort(),
    ],
    [testList],
  );
  const divisionOptions = useMemo(
    () => [
      "All",
      ...Array.from(new Set(testList.map((t) => t.division))).sort(),
    ],
    [testList],
  );
  const subjectOptions = useMemo(
    () => [
      "All",
      ...Array.from(new Set(testList.map((t) => t.subject))).sort(),
    ],
    [testList],
  );

  const filteredTests = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromTime = dateFrom
      ? new Date(dateFrom + "T00:00:00").getTime()
      : null;
    const toTime = dateTo ? new Date(dateTo + "T23:59:59").getTime() : null;

    return testList.filter((t) => {
      if (classFilter !== "All" && t.className !== classFilter) return false;
      if (divisionFilter !== "All" && t.division !== divisionFilter)
        return false;
      if (subjectFilter !== "All" && t.subject !== subjectFilter) return false;

      if (t.assignedDateObj) {
        const ts = t.assignedDateObj.getTime();
        if (fromTime !== null && ts < fromTime) return false;
        if (toTime !== null && ts > toTime) return false;
      } else if (fromTime !== null || toTime !== null) {
        // no date on record — exclude when a range filter is active
        return false;
      }

      if (!q) return true;
      const haystack =
        `${t.subject} ${t.chapter} ${t.className} ${t.division}`.toLowerCase();
      const studentMatch = t.students.some((s) =>
        s.name.toLowerCase().includes(q),
      );
      return haystack.includes(q) || studentMatch;
    });
  }, [
    testList,
    classFilter,
    divisionFilter,
    subjectFilter,
    statusFilter,
    search,
    dateFrom,
    dateTo,
  ]);

  const totals = useMemo(() => {
    const assigned = filteredTests.reduce((s, t) => s + t.assigned, 0);
    const attempted = filteredTests.reduce((s, t) => s + t.attempted, 0);
    const avg = filteredTests.length
      ? Math.round(
          filteredTests.reduce((s, t) => s + t.avgScore, 0) /
            filteredTests.length,
        )
      : 0;
    return {
      tests: filteredTests.length,
      assigned,
      attempted,
      attendancePct: assigned ? Math.round((attempted / assigned) * 100) : 0,
      avgScore: avg,
    };
  }, [filteredTests]);

  // keep selection valid as filters change (drop ids no longer visible)
  useEffect(() => {
    setSelectedIds((prev) => {
      const visibleIds = new Set(filteredTests.map((t) => t.id));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (visibleIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [filteredTests]);

  const allVisibleSelected =
    filteredTests.length > 0 &&
    filteredTests.every((t) => selectedIds.has(t.id));

  function toggleSelectAll() {
    setSelectedIds((prev) => {
      if (allVisibleSelected) return new Set();
      return new Set(filteredTests.map((t) => t.id));
    });
  }

  function toggleSelectOne(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function openPreview(test, student) {
    setSelectedTest({ ...test, _studentName: student?.name });
    setDetailQuestions(
      student?.answers || student?.questions || test.questions || [],
    );
  }

  /* ── Report Builder logic ── */

  const REPORT_TYPES = [
    {
      key: "assignment",
      title: "Assignment Report",
      icon: <FileQuestion size={20} />,
      description:
        "Test-by-test breakdown: student roster, marks and the full report for every assignment.",
    },
    {
      key: "student",
      title: "Student Report",
      icon: <Users size={20} />,
      description:
        "One row per student with attendance %, average score and a full test-by-test history rolled up.",
    },
    {
      key: "class",
      title: "Class Summary Report",
      icon: <TrendingUp size={20} />,
      description:
        "Aggregated attendance and average score by Class, Division and Subject — a management-level view.",
    },
  ];

  const scopeTests = useMemo(
    () => ({
      all: testList,
      filtered: filteredTests,
      selected: testList.filter((t) => selectedIds.has(t.id)),
    }),
    [testList, filteredTests, selectedIds],
  );

  const SCOPE_OPTIONS = [
    { key: "all", label: "All tests", count: testList.length },
    {
      key: "filtered",
      label: "Tests matching current filters",
      count: filteredTests.length,
    },
    { key: "selected", label: "Selected tests only", count: selectedIds.size },
  ];

  function handleGenerateReport() {
    const tests = scopeTests[reportScope];

    if (!tests?.length) {
      alert("There is no data available for the selected scope yet.");
      return;
    }
    setGenerating(true);
    try {
      if (reportType === "assignment") {
        exportAssignmentReport(tests, teacherInfo, "Assignment_Report");
      } else if (reportType === "student") {
        exportStudentReport(tests, teacherInfo, "Student_Report");
      } else if (reportType === "class") {
        exportClassSummaryReport(tests, teacherInfo, "Class_Summary_Report");
      }
      setBuilderOpen(false);
    } catch (err) {
    } finally {
      setGenerating(false);
    }
  }

  const canGenerate = (scopeTests[reportScope]?.length ?? 0) > 0;

  return (
    <div className="dv-wrapper">
      {/* Header */}
      <motion.div
        className="dv-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="dv-header-left">
          <button type="button" className="dv-back-btn" onClick={() => nav(-1)}>
            <ChevronLeft size={18} /> Back
          </button>
          <div>
            <h1 className="dv-title">Detailed Test Report</h1>
            <p className="dv-subtitle">
              Every class, every test, every student who attempted it
            </p>
          </div>
        </div>

        <button
          type="button"
          className="dv-report-builder-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setBuilderOpen(true);
          }}
        >
          <FileSpreadsheet size={16} /> Report Builder
        </button>
      </motion.div>

      {/* Summary chips */}
      <motion.div
        className="dv-summary-row"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
      >
        <SummaryChip
          icon={<FileQuestion size={16} />}
          label="Tests"
          value={totals.tests}
          color="#6366f1"
        />
        <SummaryChip
          icon={<Users size={16} />}
          label="Assigned"
          value={totals.assigned}
          color="#0891b2"
        />
        <SummaryChip
          icon={<CheckCircle2 size={16} />}
          label="Attempted"
          value={totals.attempted}
          color="#059669"
        />
        <SummaryChip
          icon={<Clock size={16} />}
          label="Attendance"
          value={`${totals.attendancePct}%`}
          color="#d97706"
        />
      </motion.div>

      {/* Filters */}
      <motion.div
        className="dv-filters"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.12 }}
      >
        <FilterSelect
          label="Class"
          value={classFilter}
          options={classOptions}
          onChange={setClassFilter}
        />
        <FilterSelect
          label="Division"
          value={divisionFilter}
          options={divisionOptions}
          onChange={setDivisionFilter}
        />
        <FilterSelect
          label="Subject"
          value={subjectFilter}
          options={subjectOptions}
          onChange={setSubjectFilter}
        />
        <div className="dv-search-box">
          <Search size={15} className="dv-search-icon" />
          <input
            placeholder="Search subject, chapter or student name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="dv-search-input"
          />
        </div>
      </motion.div>

      {/* Test table */}
      <motion.div
        className="dv-table-card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.16 }}
      >
        <div className="dv-table-head dv-row-grid dv-row-grid--checkbox">
          <span>
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAll}
              aria-label="Select all tests"
            />
          </span>
          <span>Subject</span>
          <span>Assignment ID</span>
          <span>Date</span>
          <span>Class</span>
          <span>Division</span>
          <span>Attempted/Assigned</span>
          <span>Raw Paper</span>
          <span></span>
        </div>

        {filteredTests.length === 0 ? (
          <div className="dv-empty">
            <FileQuestion size={28} />
            <span>No tests match these filters</span>
          </div>
        ) : (
          filteredTests.map((t) => {
            const p = pal(t.subject);
            const isOpen = expandedId === t.id;
            const isChecked = selectedIds.has(t.id);
            return (
              <div key={t.id} className="dv-test-row-wrap">
                <div
                  className={`dv-test-row dv-row-grid dv-row-grid--checkbox${isOpen ? " dv-test-row--open" : ""}`}
                  onClick={() => setExpandedId(isOpen ? null : t.id)}
                >
                  <span onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSelectOne(t.id)}
                      aria-label={`Select ${t.assignment_id}`}
                    />
                  </span>
                  <span>{t.assignment_id}</span>
                  <span className="dv-subject-cell">
                    <span
                      className="dv-subject-dot"
                      style={{ background: p.solid }}
                    />
                    <span>
                      <div className="dv-subject-name">{t.subject}</div>
                      <div className="dv-chapter-name">{t.chapter}</div>
                    </span>
                  </span>
                  <span>
                    <div>{t.date}</div>
                    <div className="small fw-semibold text-muted">{t.time}</div>
                  </span>
                  <span>{t.className}</span>
                  <span>{t.division}</span>
                  <span>
                    <span
                      className="dv-attempt-pill"
                      style={{
                        background: hex2rgba(p.solid, 0.12),
                        color: p.text,
                      }}
                    >
                      {t.attempted}/{t.assigned}
                    </span>
                  </span>
                  <span>
                    <button
                      type="button"
                      className="dv-view-paper-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPreview(t);
                      }}
                    >
                      <Eye size={14} /> View Paper
                    </button>
                  </span>
                  <span className="dv-row-actions">
                    <ChevronDown
                      size={16}
                      className={`dv-chevron${isOpen ? " dv-chevron--open" : ""}`}
                    />
                  </span>
                </div>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      className="dv-student-panel"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                    >
                      {t.students.length === 0 ? (
                        <div className="dv-empty dv-empty--small">
                          No students assigned yet
                        </div>
                      ) : (
                        <StudentRosterPanel test={t} onPreview={openPreview} />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </motion.div>

      {/* Question-paper preview drawer */}
      <AnimatePresence>
        {selectedTest && (
          <>
            <motion.div
              className="dv-drawer-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedTest(null)}
            />
            <motion.div
              className="dv-preview-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <div className="dv-preview-drawer-header">
                <div>
                  <h4>Preview: {selectedTest.topic || selectedTest.chapter}</h4>
                  <span className="dv-preview-drawer-sub">
                    {selectedTest.subject} · Total questions{" "}
                    {selectedTest.totalNoOfQuestions}
                    {selectedTest._studentName && (
                      <>
                        {" "}
                        · <strong>{selectedTest._studentName}</strong>
                      </>
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  className="dv-close-btn"
                  onClick={() => setSelectedTest(null)}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="dv-preview-drawer-body">
                {detailQuestions.length === 0 ? (
                  <p className="dv-no-q-msg">
                    No questions found for this test yet.
                  </p>
                ) : (
                  detailQuestions?.map((q, i) => {
                    const hasStudentAnswer = q.userAnswer !== undefined;
                    const isCorrect =
                      q?.evaluation?.toLowerCase() === "correct";
                    return (
                      <div key={i} className="dv-q-card">
                        <div className="dv-q-card-top">
                          <span className="dv-q-type-badge">{q.type}</span>
                          {hasStudentAnswer && (
                            <span
                              className="dv-q-eval-badge"
                              style={{
                                color: isCorrect ? "#059669" : "#ef4444",
                                background: isCorrect
                                  ? "rgba(5,150,105,0.1)"
                                  : "rgba(239,68,68,0.1)",
                                borderRadius: 6,
                                padding: "2px 8px",
                                fontSize: 11,
                                fontWeight: 700,
                                marginLeft: 8,
                              }}
                            >
                              {isCorrect ? "✓ Correct" : "✗ Incorrect"}
                              {q.marksAwarded != null &&
                                ` · ${q.marksAwarded}/${q.marks} Mark${q.marks !== 1 ? "s" : ""}`}
                            </span>
                          )}
                          {!hasStudentAnswer && (
                            <span className="dv-q-mark">
                              {q.marks ?? 1} Mark
                              {(q.marks ?? 1) !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>

                        <p className="dv-q-text">
                          {i + 1}. {q.text}
                        </p>

                        {q.type === "MCQ" && (
                          <div className="dv-options-grid">
                            {q?.options?.map((opt, idx) => {
                              const isCorrectOpt = opt === q?.correct;
                              const isStudentPick =
                                hasStudentAnswer && opt === q.userAnswer;
                              return (
                                <div
                                  key={idx}
                                  className={`dv-option-item${isCorrectOpt ? " dv-option-item--correct" : ""}`}
                                  style={
                                    isStudentPick && !isCorrectOpt
                                      ? {
                                          outline: "2px solid #ef4444",
                                          background: "rgba(239,68,68,0.06)",
                                        }
                                      : undefined
                                  }
                                >
                                  {opt} {isCorrectOpt && "✓"}{" "}
                                  <span
                                    style={{
                                      color: isCorrect ? "#059669" : "#ef4444",
                                    }}
                                  >
                                    {" "}
                                    {isStudentPick &&
                                      !isCorrectOpt &&
                                      "✗ (selected)"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {(q?.type === "FIB" || q?.type === "SHORT") && (
                          <>
                            {hasStudentAnswer && (
                              <div
                                className="dv-answer-box"
                                style={{
                                  marginBottom: 6,
                                  borderColor: isCorrect
                                    ? "#059669"
                                    : "#ef4444",
                                }}
                              >
                                <small
                                  style={{
                                    color: isCorrect ? "#059669" : "#ef4444",
                                  }}
                                >
                                  Student's Answer:
                                </small>
                                <span
                                  style={{
                                    color: isCorrect ? "#059669" : "#ef4444",
                                  }}
                                >
                                  {q.userAnswer || (
                                    <em style={{ opacity: 0.6 }}>
                                      No answer given
                                    </em>
                                  )}
                                </span>
                              </div>
                            )}
                            <div className="dv-answer-box">
                              <small>Correct Answer:</small>
                              <span>{q.answer}</span>
                            </div>
                          </>
                        )}

                        {q.explanation && (
                          <div className="dv-explanation">
                            <strong>Explanation:</strong> {q.explanation}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Report Builder modal ── */}
      <AnimatePresence>
        {builderOpen && (
          <>
            <motion.div
              className="dv-rb-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !generating && setBuilderOpen(false)}
            />
            <motion.div
              className="dv-rb-modal"
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <div className="dv-rb-header">
                <div className="dv-rb-header-left">
                  <div className="dv-rb-header-icon">
                    <ClipboardList size={20} />
                  </div>
                  <div>
                    <h3>Report Builder</h3>
                    <p>
                      Choose what to report on and how much data to include.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="dv-close-btn"
                  onClick={() => !generating && setBuilderOpen(false)}
                  disabled={generating}
                  aria-label="Close report builder"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="dv-rb-body">
                {/* Step 1: report type */}
                <div className="dv-rb-section">
                  <div className="dv-rb-type-list">
                    {REPORT_TYPES.map((rt) => {
                      const active = reportType === rt.key;
                      return (
                        <button
                          type="button"
                          key={rt.key}
                          className={`dv-rb-type-row${active ? " dv-rb-type-row--active" : ""}`}
                          onClick={() => setReportType(rt.key)}
                        >
                          <div className="dv-rb-type-icon">{rt.icon}</div>
                          <div className="dv-rb-type-text">
                            <div className="dv-rb-type-title">{rt.title}</div>
                            <div className="dv-rb-type-desc">
                              {rt.description}
                            </div>
                          </div>
                          <ChevronRight
                            size={16}
                            className="dv-rb-type-chevron"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Step 2: scope */}
                <div className="dv-rb-section dv-rb-section--last">
                  <div className="dv-rb-scope-list">
                    {SCOPE_OPTIONS.map((opt) => {
                      const active = reportScope === opt.key;
                      const disabled = opt.count === 0;
                      return (
                        <button
                          type="button"
                          key={opt.key}
                          disabled={disabled}
                          className={`dv-rb-scope-pill${active ? " dv-rb-scope-pill--active" : ""}`}
                          onClick={() => setReportScope(opt.key)}
                        >
                          {opt.label} · {opt.count}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="dv-rb-footer">
                <span className="dv-rb-footer-note">
                  {canGenerate
                    ? `${scopeTests[reportScope].length} test(s) will be included`
                    : "No data available for this scope"}
                </span>
                <div className="dv-rb-footer-actions">
                  <button
                    type="button"
                    className="dv-rb-cancel-btn"
                    onClick={() => setBuilderOpen(false)}
                    disabled={generating}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="dv-rb-generate-btn"
                    onClick={handleGenerateReport}
                    disabled={!canGenerate || generating}
                  >
                    <Download size={15} />
                    {generating ? "Generating…" : "Generate & Download"}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// function StudentRosterPanel({ test, onPreview }) {
//   const [search, setSearch] = useState("");

//   const filtered = useMemo(() => {
//     const q = search.trim().toLowerCase();
//     if (!q) return test.students;
//     return test.students.filter(
//       (s) =>
//         s.name.toLowerCase().includes(q) ||
//         String(s.studentID).toLowerCase().includes(q),
//     );
//   }, [test.students, search]);

//   return (
//     <div className="dv-student-table">
//       <div className="dv-student-row dv-student-row--head">
//         <span>Sr.No</span>
//         <span>Student ID</span>
//         <span>Student</span>
//         <span>Marks </span>
//         <span>Status</span>
//         <span>Test Paper</span>
//         <span></span>
//         <span>
//           <div className="dv-roster-search">
//             <Search size={13} className="dv-roster-search-icon" />
//             <input
//               placeholder="Search student or roll no…"
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//             />
//           </div>
//         </span>
//       </div>

//       {filtered.length === 0 ? (
//         <div className="dv-empty dv-empty--small">
//           No students match “{search}”
//         </div>
//       ) : (
//         filtered.map((s, i) => {
//           const badge = gradeBadge(s.percentage || 0);
//           return (
//             <div key={i} className="dv-student-row">
//               <span className="dv-muted">{i + 1}</span>
//               <span className="dv-student-name">{s.studentID}</span>
//               <span className="dv-student-name">{s.name}</span>
//               <span>
//                 {s?.status === true ? (
//                   <>
//                     {s.marks ?? "—"}/{s.maxMarks ?? "—"}
//                   </>
//                 ) : (
//                   "—"
//                 )}
//               </span>
//               <span>
//                 <span
//                   className={`dv-status-pill dv-status-pill--${s.status === true ? "done" : "pending"}`}
//                   disabled={s.status !== true}
//                 >
//                   {s.status === true ? "Attempted" : "Not Attempted"}
//                 </span>
//               </span>
//               <span>
//                 {s.status === true ? (
//                   <button
//                     type="button"
//                     className="dv-view-paper-btn dv-view-paper-btn--sm"
//                     onClick={() => onPreview(test, s)}
//                   >
//                     <Eye size={13} /> View Paper
//                   </button>
//                 ) : (
//                   <button
//                     type="button"
//                     className="dv-view-paper-btn dv-view-paper-btn--sm"
//                     disabled
//                   >
//                     N/A
//                   </button>
//                 )}
//               </span>
//               <span></span>
//               <span></span>
//             </div>
//           );
//         })
//       )}
//     </div>
//   );
// }
function StudentRosterPanel({ test, onPreview }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "attempted" | "not_attempted"

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return test.students.filter((s) => {
      const matchesSearch =
        !q ||
        s.name.toLowerCase().includes(q) ||
        String(s.studentID).toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "attempted" && s.status === true) ||
        (statusFilter === "not_attempted" && s.status !== true);

      return matchesSearch && matchesStatus;
    });
  }, [test.students, search, statusFilter]);

  return (
    <div className="dv-student-table">
      <div className="dv-student-row dv-student-row--head">
        <span>Sr.No</span>
        <span>Student ID</span>
        <span>Student</span>
        <span>Marks </span>
        <span>Status</span>
        <span>Test Paper</span>
        <span></span>
        <div className="dv-roster-controls">
      
          <div className="dv-roster-search">
            <Search size={13} className="dv-roster-search-icon" />
            <input
              placeholder="Student Name or ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
              <select
            className="dv-roster-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Students</option>
            <option value="attempted">Attempted</option>
            <option value="not_attempted">Not Attempted</option>
          </select>
          
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="dv-empty dv-empty--small">
          {search
            ? <>No students match "{search}"</>
            : "No students match the selected filter"}
        </div>
      ) : (
        filtered.map((s, i) => {
          const badge = gradeBadge(s.percentage || 0);
          return (
            <div key={i} className="dv-student-row">
              <span className="dv-muted">{i + 1}</span>
              <span className="dv-student-name">{s.studentID}</span>
              <span className="dv-student-name">{s.name}</span>
              <span>
                {s?.status === true ? (
                  <>
                    {s.marks ?? "—"}/{s.maxMarks ?? "—"}
                  </>
                ) : (
                  "—"
                )}
              </span>
              <span>
                <span
                  className={`dv-status-pill dv-status-pill--${s.status === true ? "done" : "pending"}`}
                  disabled={s.status !== true}
                >
                  {s.status === true ? "Attempted" : "Not Attempted"}
                </span>
              </span>
              <span>
                {s.status === true ? (
                  <button
                    type="button"
                    className="dv-view-paper-btn dv-view-paper-btn--sm"
                    onClick={() => onPreview(test, s)}
                  >
                    <Eye size={13} /> View Paper
                  </button>
                ) : (
                  <button
                    type="button"
                    className="dv-view-paper-btn dv-view-paper-btn--sm"
                    disabled
                  >
                    N/A
                  </button>
                )}
              </span>
              <span></span>
              <span></span>
            </div>
          );
        })
      )}
    </div>
  );
}

function SummaryChip({ icon, label, value, color }) {
  return (
    <div className="dv-summary-chip">
      <span
        className="dv-summary-chip-icon"
        style={{ color, background: hex2rgba(color, 0.12) }}
      >
        {icon}
      </span>
      <div>
        <div className="dv-summary-chip-label">{label}</div>
        <div className="dv-summary-chip-value">{value}</div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <label className="dv-filter-select">
      <span className="dv-filter-label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
